import { useState, useEffect, useCallback } from 'react';
import { tipoServicioComedorApi } from '../api/tipoServicioComedorApi';
import { showErrorToast, showSuccessToast } from '../utils/errorHandler';

/**
 * Hook personalizado para gestión de tipos de servicio de comedor
 * Proporciona estado y funciones para operaciones CRUD de tipos de servicio de comedor
 */
export const useTipoServiciosComedor = () => {
  const [tipoServiciosComedor, setTipoServiciosComedor] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Obtiene todos los tipos de servicio de comedor
   */
  const fetchTipoServiciosPorComedor = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await tipoServicioComedorApi.getAll();
      setTipoServiciosComedor(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar tipos de servicio de comedor');
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtiene un tipo de servicio de comedor específico por ID
   */
  const fetchTipoServicioComedorById = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await tipoServicioComedorApi.getById(id);
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar el tipo de servicio de comedor');
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Crea un nuevo tipo de servicio de comedor
   */
  const createTipoServicioComedor = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await tipoServicioComedorApi.create(data);
      showSuccessToast('Tipo de servicio de comedor creado exitosamente');
      
      // Refrescar la lista
      await fetchTipoServiciosPorComedor();
      
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al crear el tipo de servicio de comedor');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchTipoServiciosPorComedor]);

  /**
   * Actualiza un tipo de servicio de comedor existente
   */
  const updateTipoServicioComedor = useCallback(async (id, data) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await tipoServicioComedorApi.update(id, data);
      showSuccessToast('Tipo de servicio de comedor actualizado exitosamente');
      
      // Refrescar la lista
      await fetchTipoServiciosPorComedor();
      
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al actualizar el tipo de servicio de comedor');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchTipoServiciosPorComedor]);

  /**
   * Elimina un tipo de servicio de comedor
   */
  const deleteTipoServicioComedor = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      await tipoServicioComedorApi.delete(id);
      showSuccessToast('Tipo de servicio de comedor eliminado exitosamente');
      
      // Refrescar la lista
      await fetchTipoServiciosPorComedor();
      
      return true;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al eliminar el tipo de servicio de comedor');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchTipoServiciosPorComedor]);

  /**
   * Obtiene tipos de servicios de comedor por proyecto
   */
  const fetchTipoServiciosComedorByProyecto = useCallback(async (proyectoId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await tipoServicioComedorApi.getByProyecto(proyectoId);
      setTipoServiciosComedor(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar tipos de servicio de comedor por proyecto');
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // NOTA: No se cargan automáticamente los tipos de servicio de comedor
  // Se cargan bajo demanda cuando se necesiten en los componentes

  return {
    // Estado
    tipoServiciosComedor,
    loading,
    error,
    
    // Funciones
    fetchTipoServiciosPorComedor,
    fetchTipoServicioComedorById,
    fetchTipoServiciosComedorByProyecto,
    createTipoServicioComedor,
    updateTipoServicioComedor,
    deleteTipoServicioComedor,
    
    // Función para limpiar errores
    clearError: () => setError(null),
  };
};

export default useTipoServiciosComedor;
